import { REDACTION_RULES } from './redaction.js';
/**
 * The run-log redactor's rules, executed on this side of the boundary (IM-54).
 *
 * ## Why this exists at all
 *
 * Run logs are redacted in the customer's account, by the Python program in
 * `redaction.ts`, before a line is written — and that stays the only place a
 * log line is masked on its way *out* of their account. But a diagnostic
 * bundle carries free text that never went through that pipe: a
 * CloudFormation event's status reason, an EKS health message, a run record's
 * failure detail. Those arrive through the diagnostics role as API responses,
 * and the bundle is a file meant to be mailed to Qrvey engineering. They have
 * to be masked by *something* before that, and the acceptance criterion says
 * it must be the same thing.
 *
 * ## Same rules, not a second list
 *
 * This reads `REDACTION_RULES` — the very array the Python program is built
 * from — and translates each pattern once. There is no second list to drift,
 * and `redact.test.ts` runs the Python redactor and this one over the same
 * lines and requires them to agree character for character. A rule a future
 * story adds reaches both, or the test fails.
 *
 * ## Fails closed on a pattern it cannot translate
 *
 * The patterns are Python regular expressions. The subset in use today maps
 * onto JavaScript's exactly — a leading `(?i)`, lookbehinds, `\1` in the
 * replacement — and anything outside that subset throws at module load rather
 * than being approximated. A rule silently compiled into something that masks
 * less is the failure a redactor cannot have, and a throw here fails every
 * test that imports `shared`.
 *
 * Pure, and free of `node:` imports, because `shared` is imported into a
 * browser.
 */
export class UntranslatableRedactionRuleError extends Error {
    rule;
    constructor(rule, why) {
        super(`redaction rule ${rule} cannot be executed here: ${why}`);
        this.rule = rule;
        this.name = 'UntranslatableRedactionRuleError';
    }
}
/**
 * Python constructs with no exact JavaScript equivalent.
 *
 * `(?` followed by anything but a non-capturing group or a look-around is an
 * inline flag or a named group (`(?P<…>`), and `\A`/`\Z` are Python-only
 * anchors. None is used today; the check is here so that the day one is, this
 * refuses instead of guessing.
 */
const UNSUPPORTED = [
    { pattern: /\(\?(?![:=!]|<[=!])/, why: 'inline flag or named group' },
    { pattern: /\\[AZ]/, why: 'Python-only anchor' },
];
export const translateRule = (rule) => {
    let source = rule.pattern;
    let flags = 'g';
    // Python's leading inline flag, and only that one, becomes a JS flag.
    if (source.startsWith('(?i)')) {
        source = source.slice(4);
        flags += 'i';
    }
    for (const { pattern, why } of UNSUPPORTED) {
        if (pattern.test(source))
            throw new UntranslatableRedactionRuleError(rule.name, why);
    }
    // `$` is literal in a Python replacement and special in a JS one; escape it
    // first, then turn Python's `\1` into JS's `$1`.
    const replacement = rule.replacement.replace(/\$/g, '$$$$').replace(/\\(\d+)/g, '$$$1');
    if (/\\[^\d]/.test(replacement.replace(/\$\$/g, ''))) {
        throw new UntranslatableRedactionRuleError(rule.name, 'escape in replacement');
    }
    try {
        return { name: rule.name, pattern: new RegExp(source, flags), replacement };
    }
    catch (error) {
        throw new UntranslatableRedactionRuleError(rule.name, error instanceof Error ? error.message : String(error));
    }
};
/** Compiled once, at load, so an untranslatable rule fails loudly and early. */
const COMPILED = REDACTION_RULES.map(translateRule);
/**
 * One line, through every rule in order — exactly what the Python loop does.
 *
 * Order matters, and is the rules' own: `uri-credentials` has to run before
 * `email-local-part`, which is why it is listed first there.
 */
export const redactLine = (line) => COMPILED.reduce((text, rule) => text.replace(rule.pattern, rule.replacement), line);
/**
 * Free text of any length, a line at a time.
 *
 * Per line because the Python redactor reads stdin per line and its `^`
 * lookbehind means "start of the line" — a whole multi-line string treated as
 * one would give `^` a different meaning than the customer's account gives it.
 */
export const redactText = (text) => text.split('\n').map(redactLine).join('\n');
//# sourceMappingURL=redact.js.map